import { PartnerPortfolioListTemplate } from "~mangomap-ui/templates/partner-portfolio-list-template";
import { PartnerCreateAccountTemplate } from "~mangomap-ui/templates/partner-create-account-template";
import { emptyPortfolioFilters } from "~mangomap-ui/templates/partner-templates/types";
import { getRecurlyHostedBillingInfoUrl } from "~utils/recurly/get-recurly-hosted-billing-info-url";
import type {
	CreateManagedAccountInput,
	PartnerWriteError,
	PortfolioSortKey,
} from "~mangomap-ui/templates/partner-templates/types";
import { useQueryClient } from "@tanstack/react-query";
import { type NextPage } from "next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import {
	PartnerAuthRequired,
	PartnerDashboardPageWrapper,
	usePartnerAuth,
} from "~/auth";
import { useCreateManagedAccount } from "~/queries/use-create-managed-account";
import {
	getPartnerManagedAccountsQueryKey,
	useGetPartnerManagedAccounts,
} from "~/queries/use-get-partner-managed-accounts";
import { toAccount } from "~/queries/managed-account-mapper";
import {
	useCancelManagedAccountSubscription,
	useDeleteManagedAccount,
	useEndManagedAccountTrialEarly,
	useRestartManagedAccountTrial,
} from "~/queries/use-managed-account-subscription";
import { useOpenManagedAccountSession } from "~/queries/use-open-managed-account-session";
import { GetPartnerManagedAccountsQueryVariables } from "~/types/__generated/gql/graphql";

const DEFAULT_CREATE_ACCOUNT_INPUT: CreateManagedAccountInput = {
	companyName: "",
	slug: "",
	clientOwnerEmail: "",
	clientOwnerName: "",
	mangomapSupportAccess: false,
};

const PortfoliosPage: NextPage = () => {
	const router = useRouter();
	const queryClient = useQueryClient();
	const { partner } = usePartnerAuth();
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [filters, setFilters] = useState(emptyPortfolioFilters);
	const [sort, setSort] = useState<PortfolioSortKey>("newest");
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [managingAccountId, setManagingAccountId] = useState<string | null>(
		null,
	);
	const [managingErrorMessage, setManagingErrorMessage] = useState<
		string | null
	>(null);
	const [createAttemptedSubmit, setCreateAttemptedSubmit] = useState(false);
	const [createValues, setCreateValues] = useState<CreateManagedAccountInput>(
		DEFAULT_CREATE_ACCOUNT_INPUT,
	);
	const variables: GetPartnerManagedAccountsQueryVariables = {
		offset: page * rowsPerPage,
		limit: rowsPerPage,
		statusFilter: filters.status ? [filters.status] : [],
		planFilter: filters.plan,
		cycleFilter: filters.cycle,
		search: filters.search || undefined,
		sort,
	};
	const managedAccountsQuery = useGetPartnerManagedAccounts(variables);
	const createManagedAccountMutation = useCreateManagedAccount();
	const cancelManagedAccountSubscriptionMutation =
		useCancelManagedAccountSubscription();
	const endManagedAccountTrialEarlyMutation = useEndManagedAccountTrialEarly();
	const restartManagedAccountTrialMutation = useRestartManagedAccountTrial();
	const deleteManagedAccountMutation = useDeleteManagedAccount();
	const openManagedAccountSessionMutation = useOpenManagedAccountSession();
	const accounts = (managedAccountsQuery.data?.items ?? []).map(toAccount);
	const submitError = toCreatePartnerWriteError(
		createManagedAccountMutation.error,
	);
	const fieldErrors = getCreateAccountFieldErrors(
		createValues,
		submitError,
		createManagedAccountMutation.isPending,
		createAttemptedSubmit,
	);
	const isSuspended = Boolean(partner?.isSuspended);
	const [confirmingAction, setConfirmingAction] = useState<{
		type: "end-trial" | "cancel" | "delete";
		account: (typeof accounts)[number];
	} | null>(null);

	useEffect(() => {
		if (!createManagedAccountMutation.isSuccess) return;
		const timeoutId = window.setTimeout(() => {
			setCreateModalOpen(false);
			setCreateAttemptedSubmit(false);
			setCreateValues(DEFAULT_CREATE_ACCOUNT_INPUT);
			createManagedAccountMutation.reset();
		}, 1200);
		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [createManagedAccountMutation]);

	const handleCreateAccount = async (input: CreateManagedAccountInput) => {
		setCreateAttemptedSubmit(true);
		const localErrors = getCreateAccountFieldErrors(input, null, false, true);
		if (Object.keys(localErrors).length > 0) return;

		await createManagedAccountMutation.mutateAsync(input);
		await queryClient.invalidateQueries({
			queryKey: getPartnerManagedAccountsQueryKey(variables),
		});
		await queryClient.invalidateQueries({
			queryKey: ["partnerManagedAccounts"],
		});
	};

	const handleConfirmAction = async () => {
		if (!confirmingAction) return;
		if (confirmingAction.type === "cancel") {
			await cancelManagedAccountSubscriptionMutation.mutateAsync(
				confirmingAction.account.id,
			);
		}
		if (confirmingAction.type === "end-trial") {
			await endManagedAccountTrialEarlyMutation.mutateAsync(
				confirmingAction.account.id,
			);
		}
		if (confirmingAction.type === "delete") {
			await deleteManagedAccountMutation.mutateAsync(
				confirmingAction.account.id,
			);
		}
		setConfirmingAction(null);
		await queryClient.invalidateQueries({
			queryKey: ["partnerManagedAccounts"],
		});
	};

	const handleManageAccount = async (accountId: string) => {
		setManagingErrorMessage(null);
		setManagingAccountId(accountId);
		try {
			const result =
				await openManagedAccountSessionMutation.mutateAsync(accountId);
			const launchUrl = result.launchUrl;
			window.open(launchUrl, "_blank", "noopener,noreferrer");
		} catch (error) {
			console.error("Open managed account session failed", error);
			setManagingErrorMessage(
				"Unable to open this managed account session right now. Please try again.",
			);
		} finally {
			setManagingAccountId(null);
		}
	};

	return (
		<PartnerAuthRequired>
			<PartnerDashboardPageWrapper>
				<NextSeo title="Client Accounts" />
				<PartnerPortfolioListTemplate
					accounts={accounts}
					totalCount={managedAccountsQuery.data?.total ?? 0}
					statusCounts={
						managedAccountsQuery.data?.statusCounts
							? {
									...managedAccountsQuery.data.statusCounts,
								}
							: {
									ALL: 0,
									TRIAL: 0,
									PAID: 0,
									PAST_DUE: 0,
									CLOSED: 0,
								}
					}
					loadingFirstPage={managedAccountsQuery.isLoading}
					errorMessage={
						managedAccountsQuery.error instanceof Error
							? managedAccountsQuery.error.message
							: null
					}
					onRetryLoad={() => void managedAccountsQuery.refetch()}
					filters={filters}
					sort={sort}
					page={page}
					rowsPerPage={rowsPerPage}
					onFiltersChange={setFilters}
					onClearFilters={() => setFilters(emptyPortfolioFilters)}
					onSortChange={setSort}
					suspended={isSuspended}
					onCreateAccount={() => setCreateModalOpen(true)}
					onOpenAccount={(id) =>
						void router.push(`/portfolios/${encodeURIComponent(id)}`)
					}
					onManageAccount={(id) => void handleManageAccount(id)}
					managingAccountId={managingAccountId}
					managingErrorMessage={managingErrorMessage}
					onOpenSubscription={(id) =>
						void router.push(
							`/portfolios/${encodeURIComponent(id)}/subscription`,
						)
					}
					onRestartTrial={async (id) => {
						await restartManagedAccountTrialMutation.mutateAsync(id);
						await queryClient.invalidateQueries({
							queryKey: ["partnerManagedAccounts"],
						});
					}}
					onRequestCancelSubscription={(id) => {
						const account = accounts.find((item) => item.id === id);
						if (!account) return;
						setConfirmingAction({ type: "cancel", account });
					}}
					onRequestEndTrial={(id) => {
						const account = accounts.find((item) => item.id === id);
						if (!account) return;
						setConfirmingAction({ type: "end-trial", account });
					}}
					onRequestDeleteAccount={(id) => {
						const account = accounts.find((item) => item.id === id);
						if (!account) return;
						setConfirmingAction({ type: "delete", account });
					}}
					confirmingAction={confirmingAction}
					confirmationLoading={
						cancelManagedAccountSubscriptionMutation.isPending ||
						endManagedAccountTrialEarlyMutation.isPending ||
						deleteManagedAccountMutation.isPending
					}
					confirmationError={toPartnerWriteError(
						cancelManagedAccountSubscriptionMutation.error ??
							endManagedAccountTrialEarlyMutation.error ??
							deleteManagedAccountMutation.error,
					)}
					onConfirmAction={() => void handleConfirmAction()}
					onCloseConfirmation={() => setConfirmingAction(null)}
					onPaginationChange={({
						page: nextPage,
						rowsPerPage: nextRowsPerPage,
					}) => {
						setPage(nextPage);
						setRowsPerPage(nextRowsPerPage);
					}}
				/>
				<PartnerCreateAccountTemplate
					open={createModalOpen}
					values={createValues}
					fieldErrors={fieldErrors}
					submitting={createManagedAccountMutation.isPending}
					submitSuccess={createManagedAccountMutation.isSuccess}
					submitError={submitError}
					suspended={isSuspended}
					trialDefaults={{ plan: "ENTERPRISE", lengthDays: 30 }}
					onChange={setCreateValues}
					onSubmit={(input) => void handleCreateAccount(input)}
					onCancel={() => {
						if (createManagedAccountMutation.isPending) return;
						setCreateModalOpen(false);
						setCreateAttemptedSubmit(false);
						setCreateValues(DEFAULT_CREATE_ACCOUNT_INPUT);
						createManagedAccountMutation.reset();
					}}
					onSetPaymentMethod={() => {
						const hostedToken = partner?._recurlyAccountInfo?.hostedToken;
						if (!hostedToken) return;
						window.open(
							getRecurlyHostedBillingInfoUrl(hostedToken),
							"_blank",
							"noopener,noreferrer",
						);
					}}
				/>
			</PartnerDashboardPageWrapper>
		</PartnerAuthRequired>
	);
};

export default PortfoliosPage;

const getCreateAccountFieldErrors = (
	values: CreateManagedAccountInput,
	error: PartnerWriteError | null,
	submitting: boolean,
	validateLocally: boolean,
) => {
	if (submitting) return {};
	const errors: {
		companyName?: string;
		slug?: string;
		clientOwnerEmail?: string;
		clientOwnerName?: string;
	} = {};

	if (validateLocally) {
		if (!values.companyName.trim())
			errors.companyName = "Company name is required.";
		if (!values.slug.trim()) {
			errors.slug = "Slug is required.";
		} else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(values.slug.trim())) {
			errors.slug =
				"Slug may only contain lower-case letters, numbers, and hyphens.";
		}
		if (!values.clientOwnerEmail.trim()) {
			errors.clientOwnerEmail = "Owner email is required.";
		} else if (
			!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.clientOwnerEmail.trim())
		) {
			errors.clientOwnerEmail = "Enter a valid email address.";
		}
	}

	if (error?.__type === "WriteFailedError") {
		if (error.code === "SLUG_TAKEN") {
			errors.slug = `"${values.slug}" is already taken.`;
		}
		if (error.code === "EMAIL_TAKEN") {
			errors.clientOwnerEmail = "That email is already in use.";
		}
		if (error.code === "EMAIL_INVALID") {
			errors.clientOwnerEmail = "Enter a valid email address.";
		}
		if (error.code === "SLUG_INVALID") {
			errors.slug =
				"Slug may only contain lower-case letters, numbers, and hyphens.";
		}
	}

	return errors;
};

const toCreatePartnerWriteError = (
	error: unknown,
): PartnerWriteError | null => {
	if (!(error instanceof Error)) return null;
	const code = error.message;

	if (code === "PAYMENT_METHOD_REQUIRED") {
		return {
			__type: "PaymentMethodRequiredError",
			code: "PAYMENT_METHOD_REQUIRED",
			userMessage: "Set a payment method before creating client accounts.",
			retryable: false,
		};
	}

	if (code === "SLUG_TAKEN" || code === "EMAIL_TAKEN") {
		return {
			__type: "WriteFailedError",
			code,
			userMessage:
				code === "SLUG_TAKEN"
					? "That account slug is already in use."
					: "That email is already in use.",
			retryable: false,
		};
	}

	if (code === "SLUG_INVALID" || code === "EMAIL_INVALID") {
		return {
			__type: "WriteFailedError",
			code,
			userMessage:
				code === "SLUG_INVALID"
					? "Slug format is invalid."
					: "Email format is invalid.",
			retryable: false,
		};
	}

	if (code === "PARTNER_SUSPENDED") {
		return {
			__type: "WriteFailedError",
			code: "GENERIC",
			userMessage:
				"Your partner account is suspended; new client accounts cannot be created.",
			retryable: false,
		};
	}

	return {
		__type: "WriteFailedError",
		code: "GENERIC",
		userMessage: "Something went wrong creating that account. Try again.",
		retryable: true,
	};
};

const toPartnerWriteError = (error: unknown): PartnerWriteError | null => {
	if (!(error instanceof Error)) return null;
	const code = error.message;

	if (code === "PARTNER_SUSPENDED") {
		return {
			__type: "WriteFailedError",
			code: "GENERIC",
			userMessage: "Your partner account is suspended.",
			retryable: false,
		};
	}

	if (
		code === "ACCOUNT_NOT_OWNED" ||
		code === "UNSUPPORTED_BILLING_MODEL" ||
		code === "RECURLY_UNAVAILABLE" ||
		code === "RECURLY_DECLINED"
	) {
		const normalizedCode =
			code === "UNSUPPORTED_BILLING_MODEL" ? "GENERIC" : code;
		return {
			__type: "WriteFailedError",
			code: normalizedCode,
			userMessage:
				code === "ACCOUNT_NOT_OWNED"
					? "You can only manage subscriptions for accounts in your portfolio."
					: code === "UNSUPPORTED_BILLING_MODEL"
						? "This action is no longer available for the current account state."
						: code === "RECURLY_DECLINED"
							? "The billing provider declined this cancellation request."
							: "The billing provider is temporarily unavailable.",
			retryable: code !== "ACCOUNT_NOT_OWNED",
		};
	}

	return {
		__type: "WriteFailedError",
		code: "GENERIC",
		userMessage: "Unable to process request right now.",
		retryable: true,
	};
};
